import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveSettings } from '../../api/settingsApi';
import { useSettings } from '../../context/SettingsContext';
import useIsMobile from '../../utils/useIsMobile';

const inputBase = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
  color: '#F3F4F6', fontSize: '14px', fontFamily: 'inherit', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.2s',
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const { settings, refresh, setSettings } = useSettings();
  const isMobile = useIsMobile();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [form, setForm] = useState({
    site_name: '', email: '', phone: '', address: '', map_link: '',
    facebook: '', instagram: '', twitter: '', linkedin: '',
    hero_title: '', hero_subtitle: '', footer_text: '', copyright: '',
  });

  const [previews, setPreviews] = useState({ logo: null, favicon: null, hero_image: null });
  const fileRefs = { logo: useRef(null), favicon: useRef(null), hero_image: useRef(null) };

  useEffect(() => {
    if (!settings) return;
    setForm({
      site_name: settings.site_name || '', email: settings.email || '', phone: settings.phone || '',
      address: settings.address || '', map_link: settings.map_link || '',
      facebook: settings.facebook || '', instagram: settings.instagram || '',
      twitter: settings.twitter || '', linkedin: settings.linkedin || '',
      hero_title: settings.hero_title || '', hero_subtitle: settings.hero_subtitle || '',
      footer_text: settings.footer_text || '', copyright: settings.copyright || '',
    });
    setPreviews({
      logo: settings.logo_url || null, favicon: settings.favicon_url || null,
      hero_image: settings.hero_image_url || null,
    });
  }, [settings]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onPick = (k) => fileRefs[k].current?.click();
  const onFile = (k) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreviews((p) => ({ ...p, [k]: URL.createObjectURL(f) }));
  };

  const onSave = async () => {
    setSaving(true); setErr(''); setSuccess(false);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.set(k, v));
      if (fileRefs.logo.current?.files?.[0]) fd.set('logo', fileRefs.logo.current.files[0]);
      if (fileRefs.favicon.current?.files?.[0]) fd.set('favicon', fileRefs.favicon.current.files[0]);
      if (fileRefs.hero_image.current?.files?.[0]) fd.set('hero_image', fileRefs.hero_image.current.files[0]);
      const updated = await saveSettings(fd);
      setSettings(updated);
      await refresh();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) { setErr(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'contact', label: 'Contact', icon: 'Phone' },
    { id: 'social', label: 'Social', icon: 'Globe' },
    { id: 'home', label: 'Homepage', icon: 'Home' },
    { id: 'footer', label: 'Footer', icon: 'ChevronDown' },
  ];

  const onFocus = (e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.5)'; };
  const onBlur = (e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; };

  const renderField = (label, value, onChange, placeholder, type = 'text') => (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={onChange} placeholder={placeholder} rows={3}
          style={{ ...inputBase, resize: 'vertical' }} onFocus={onFocus} onBlur={onBlur} />
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
          style={inputBase} onFocus={onFocus} onBlur={onBlur} />
      )}
    </div>
  );

  const renderImageUpload = (label, key) => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <label style={{ fontSize: '13px', fontWeight: 500, color: '#9CA3AF' }}>{label}</label>
        <button type="button" onClick={() => onPick(key)}
          style={{ padding: '7px 16px', borderRadius: '6px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: '#FFD700', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
          Upload
        </button>
        <input ref={fileRefs[key]} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile(key)} />
      </div>
      {previews[key] && (
        <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,215,0,0.2)' }}>
          <img src={previews[key]} alt="" style={{ width: '100%', height: key === 'favicon' ? '80px' : '140px', objectFit: key === 'favicon' ? 'contain' : 'cover', display: 'block', background: 'rgba(255,255,255,0.03)' }} />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#F9FAFB' }}>Settings</h1>
          <p style={{ margin: '6px 0 0', color: '#9CA3AF', fontSize: '14px' }}>Manage website branding, content, and configuration</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/admin-dashboard')} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#D1D5DB', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            Dashboard
          </button>
          <button onClick={onSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#0B1020', fontWeight: 600, fontSize: '14px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {err && (
        <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', fontSize: '14px', marginBottom: '20px' }}>{err}</div>
      )}

      {success && (
        <div style={{ padding: '14px 16px', background: 'rgba(52,211,153,0.1)', borderRadius: '10px', border: '1px solid rgba(52,211,153,0.2)', color: '#6EE7B7', fontSize: '14px', marginBottom: '20px', fontWeight: 500 }}>
          Settings saved successfully!
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '9px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === tab.id ? 600 : 500,
              fontFamily: 'inherit',
              background: activeTab === tab.id ? 'rgba(255,215,0,0.12)' : 'rgba(255,255,255,0.03)',
              color: activeTab === tab.id ? '#FFD700' : '#6B7280',
              transition: 'all 0.2s',
              border: activeTab === tab.id ? '1px solid rgba(255,215,0,0.2)' : '1px solid transparent'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ background: '#0F1B2D', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.05)', padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {activeTab === 'general' && (
            <>
              {renderField('Website Name', form.site_name, onChange('site_name'), 'FACTO')}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                {renderImageUpload('Logo', 'logo')}
                {renderImageUpload('Favicon', 'favicon')}
              </div>
            </>
          )}

          {activeTab === 'contact' && (
            <>
              {renderField('Email', form.email, onChange('email'), 'info@example.com')}
              {renderField('Phone Number', form.phone, onChange('phone'), '+91 ...')}
              {renderField('Address', form.address, onChange('address'), 'Full address', 'textarea')}
              {renderField('Google Map Link', form.map_link, onChange('map_link'), 'https://maps.google.com/...')}
            </>
          )}

          {activeTab === 'social' && (
            <>
              {renderField('Facebook URL', form.facebook, onChange('facebook'), 'https://facebook.com/...')}
              {renderField('Instagram URL', form.instagram, onChange('instagram'), 'https://instagram.com/...')}
              {renderField('Twitter / X URL', form.twitter, onChange('twitter'), 'https://x.com/...')}
              {renderField('LinkedIn URL', form.linkedin, onChange('linkedin'), 'https://linkedin.com/in/...')}
            </>
          )}

          {activeTab === 'home' && (
            <>
              {renderField('Hero Title', form.hero_title, onChange('hero_title'), 'Hero title')}
              {renderField('Hero Subtitle', form.hero_subtitle, onChange('hero_subtitle'), 'Hero subtitle', 'textarea')}
              {renderImageUpload('Hero Image', 'hero_image')}
            </>
          )}

          {activeTab === 'footer' && (
            <>
              {renderField('Footer Text', form.footer_text, onChange('footer_text'), 'Footer description', 'textarea')}
              {renderField('Copyright Text', form.copyright, onChange('copyright'), '© 2026 ...')}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AdminSettings;
